import React, { createContext, useContext, useReducer } from 'react';

const GameContext = createContext();

// Gewichtete Rundentypen (höhere Zahl = häufiger)
const WEIGHTED_ROUND_TYPES = [
  { type: 'REGEL', weight: 1 },
  { type: 'FRAGE', weight: 3 },
  { type: 'PANTOMIME', weight: 3 },
  { type: 'ZEICHNEN', weight: 3 },
  { type: 'WER_HAT', weight: 3 },
  { type: 'ZUFALLSEREIGNIS', weight: 2 },
  { type: 'DUELL', weight: 3 },
  { type: 'AUFZAEHLUNG', weight: 3 }
];

const initialState = {
  players: [],
  currentRound: 0,
  roundHistory: [],
  activePlayer: null,
  gameStarted: false,
  currentRoundType: null,
  activeRules: {
    global: null,
    personal: []
  },
  drinkingBuddies: null, // null or [player1, player2]
  enabledRounds: {
    REGEL: true,
    FRAGE: true,
    PANTOMIME: true,
    ZEICHNEN: true,
    WER_HAT: true,
    ZUFALLSEREIGNIS: true,
    DUELL: true,
    AUFZAEHLUNG: true
  }
};

function getRandomRoundType(history = [], enabledRounds = {}) {
  // Erstelle ein Array mit gewichteten Typen, aber nur mit aktivierten Runden
  const weightedTypes = WEIGHTED_ROUND_TYPES.reduce((acc, { type, weight }) => {
    if (enabledRounds[type]) {
      return [...acc, ...Array(weight).fill(type)];
    }
    return acc;
  }, []);

  // Vermeide die gleiche Runde zweimal hintereinander
  let availableTypes = weightedTypes;
  if (history.length > 0) {
    const lastRound = history[history.length - 1];
    availableTypes = weightedTypes.filter(type => type !== lastRound);
  }

  return availableTypes[Math.floor(Math.random() * availableTypes.length)];
}

function gameReducer(state, action) {
  switch (action.type) {
    case 'ADD_PLAYER':
      return {
        ...state,
        players: [...state.players, { name: action.payload, score: 0, drinks: 0 }]
      };
    case 'START_GAME':
      const firstRoundType = getRandomRoundType([], state.enabledRounds);
      return {
        ...state,
        gameStarted: true,
        activePlayer: state.players[0]?.name || null,
        currentRoundType: firstRoundType,
        roundHistory: [firstRoundType]
      };
    case 'NEXT_ROUND': {
      const nextRoundType = getRandomRoundType(state.roundHistory, state.enabledRounds);
      return {
        ...state,
        currentRound: state.currentRound + 1,
        activePlayer: state.players[state.currentRound % state.players.length]?.name,
        currentRoundType: nextRoundType,
        roundHistory: [...state.roundHistory, nextRoundType]
      };
    }
    case 'SET_DRINKING_BUDDIES':
      return {
        ...state,
        drinkingBuddies: action.payload // [player1, player2] or null
      };
    case 'UPDATE_SCORE': {
      const updatedPlayers = state.players.map(player => {
        if (player.name === action.payload.player) {
          return { 
            ...player, 
            score: player.score + action.payload.score,
            drinks: player.drinks + (action.payload.drinks || 0)
          };
        }
        // If this player is a drinking buddy, they also drink
        if (state.drinkingBuddies && 
            state.drinkingBuddies.includes(action.payload.player) &&
            state.drinkingBuddies.includes(player.name)) {
          return {
            ...player,
            drinks: player.drinks + (action.payload.drinks || 0)
          };
        }
        return player;
      });
      return {
        ...state,
        players: updatedPlayers
      };
    }
    case 'SET_GLOBAL_RULE':
      return {
        ...state,
        activeRules: {
          ...state.activeRules,
          global: action.payload
        }
      };
    case 'ADD_PERSONAL_RULE': {
      const newPersonalRules = [...state.activeRules.personal];
      if (newPersonalRules.length >= 2) {
        // Entferne die älteste persönliche Regel
        newPersonalRules.shift();
      }
      // Füge die neue Regel hinzu
      newPersonalRules.push(action.payload);
      return {
        ...state,
        activeRules: {
          ...state.activeRules,
          personal: newPersonalRules
        }
      };
    }
    case 'TOGGLE_ROUND_TYPE':
      return {
        ...state,
        enabledRounds: {
          ...state.enabledRounds,
          [action.payload]: !state.enabledRounds[action.payload]
        }
      };
    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  const value = {
    state,
    addPlayer: (name) => dispatch({ type: 'ADD_PLAYER', payload: name }),
    startGame: () => dispatch({ type: 'START_GAME' }),
    nextRound: () => dispatch({ type: 'NEXT_ROUND' }),
    updateScore: (player, score, drinks = 0) => 
      dispatch({ type: 'UPDATE_SCORE', payload: { player, score, drinks } }),
    setGlobalRule: (rule) =>
      dispatch({ type: 'SET_GLOBAL_RULE', payload: rule }),
    addPersonalRule: (rule) =>
      dispatch({ type: 'ADD_PERSONAL_RULE', payload: rule }),
    toggleRoundType: (roundType) =>
      dispatch({ type: 'TOGGLE_ROUND_TYPE', payload: roundType }),
    setDrinkingBuddies: (players) =>
      dispatch({ type: 'SET_DRINKING_BUDDIES', payload: players })
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame muss innerhalb eines GameProviders verwendet werden');
  }
  return context;
} 